'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import { useTranslations } from 'next-intl';
import { IconShieldCheck } from '@tabler/icons-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PaymentVerificationCancelledError, registerPaymentVerifier } from '@/lib/payment-risk/client';

export default function PaymentSecurityProvider() {
  const t = useTranslations('PaymentSecurity');
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [interactive, setInteractive] = useState(false);
  const [container, setContainer] = useState(null);
  const [generation, setGeneration] = useState(0);
  const content = useRef(null);
  const pending = useRef(null);

  const finish = useCallback((token, failure, request = pending.current) => {
    if (!request || request !== pending.current) return;
    pending.current = null;
    clearTimeout(request.timer);
    setOpen(false);
    if (failure) request.reject(failure instanceof Error ? failure : new Error(failure));
    else request.resolve(token);
  }, []);

  useEffect(() => {
    const unregister = registerPaymentVerifier(() => new Promise((resolve, reject) => {
      const request = { resolve, reject, timer: null };
      request.timer = setTimeout(() => finish(null, t('timeout'), request), 30000);
      pending.current = request;
      setGeneration(value => value + 1);
      setInteractive(false);
      setOpen(true);
    }));
    return () => { unregister(); finish(null, new PaymentVerificationCancelledError(t('cancelled'))); };
  }, [t, finish]);

  useEffect(() => {
    if (!open || !container || (!ready && !window.turnstile)) return;
    const request = pending.current;
    if (!request) return;
    const complete = (token, failure) => finish(token, failure, request);
    let widget;
    try {
      // The callback ref reruns this effect when Radix actually mounts its portal.
      widget = window.turnstile.render(container, {
        sitekey: process.env.NEXT_PUBLIC_PAYMENT_TURNSTILE_SITE_KEY
          || process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY || '0x4AAAAAABCJfotJVlXxjnKC',
        action: 'payment', theme: 'dark', appearance: 'interaction-only',
        retry: 'never',
        callback: token => complete(token),
        'before-interactive-callback': () => {
          if (request !== pending.current) return;
          clearTimeout(request.timer);
          request.timer = setTimeout(() => complete(null, t('timeout')), 120000);
          setInteractive(true);
        },
        'error-callback': () => { complete(null, t('unavailable')); return true; },
        'unsupported-callback': () => complete(null, t('unavailable')),
        'timeout-callback': () => complete(null, t('timeout')),
        'expired-callback': () => { if (widget !== undefined) window.turnstile.reset(widget); },
      });
    } catch {
      complete(null, t('unavailable'));
    }
    return () => { if (widget !== undefined && window.turnstile) window.turnstile.remove(widget); };
  }, [open, ready, container, generation, finish, t]);

  useEffect(() => {
    if (interactive) content.current?.focus();
  }, [interactive]);

  return (
    <>
      {open && <Script id="payment-turnstile" src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit" onReady={() => setReady(true)} onError={() => finish(null, t('unavailable'))} />}
      <Dialog open={open} onOpenChange={value => { if (!value) finish(null, new PaymentVerificationCancelledError(t('cancelled'))); }}>
        <DialogContent
          ref={content}
          aria-describedby={undefined}
          data-payment-verification={interactive ? 'interactive' : 'automatic'}
          overlayClassName={`z-[300] ${interactive ? 'bg-black/80' : 'bg-transparent'}`}
          className={`z-[310] w-full max-w-[350px] rounded-lg text-white ${interactive ? 'border-white/10 bg-zinc-950 [&>button]:right-6 [&>button]:top-7' : 'border-0 bg-transparent p-0 shadow-none [&>button]:hidden'}`}
          onOpenAutoFocus={event => event.preventDefault()}
        >
          <DialogHeader className={interactive ? 'flex-row items-center gap-3 space-y-0 pr-6 text-left' : 'sr-only'}>
            <IconShieldCheck size={24} className="shrink-0 text-amber-400" />
            <DialogTitle className="leading-6">{t('title')}</DialogTitle>
          </DialogHeader>
          <div ref={setContainer} className={interactive ? 'flex min-h-16 min-w-0 justify-center [&>div]:shrink-0 [&>iframe]:shrink-0' : undefined} />
        </DialogContent>
      </Dialog>
    </>
  );
}
